import _extends from "@babel/runtime/helpers/esm/extends";
import _inheritsLoose from "@babel/runtime/helpers/esm/inheritsLoose";

var _dec, _class, _class2, _temp;

import React from 'react';
import PropTypes from 'prop-types';
import { globalObject, isBrowser } from '@skbkontur/global-object';
import { responsiveLayout } from "../../../components/ResponsiveLayout/decorator";
import { CalendarDataTids } from "../../../components/Calendar/Calendar";
import { getRandomID, isNonNullable } from "../../../lib/utils";
import { isKeyEscape } from "../../../lib/events/keyboard/identifiers";
import { DatePickerLocaleHelper } from "../../../components/DatePicker/locale";
import { locale } from "../../../lib/locale/decorators";
import { RenderLayer } from "../../RenderLayer";
import { DropdownContainer } from "../../DropdownContainer";
import * as LayoutEvents from "../../../lib/LayoutEvents";
import { ThemeContext } from "../../../lib/theming/ThemeContext";
import { ArrowTriangleUpDownIcon, ArrowChevronDownIcon, ArrowChevronUpIcon } from "../../icons/16px";
import { isMobile } from "../../../lib/client";
import { cx } from "../../../lib/theming/Emotion";
import { getDOMRect } from "../../../lib/dom/getDOMRect";
import { createPropsGetter } from "../../../lib/createPropsGetter";
import { isTheme2022 } from "../../../lib/theming/ThemeHelpers";
import { ArrowCollapseCVOpenIcon16Regular } from "../../icons2022/ArrowCollapseCVOpenIcon/ArrowCollapseCVOpenIcon16Regular";
import { ArrowCUpIcon16Regular } from "../../icons2022/ArrowCUpIcon/ArrowCUpIcon16Regular";
import { ArrowCDownIcon16Regular } from "../../icons2022/ArrowCDownIcon/ArrowCDownIcon16Regular";
import { isInstanceOf } from "../../../lib/isInstanceOf";
import { globalClasses, styles } from "../DateSelect.styles";
var itemHeight = 24;
var visibleYearsCount = 11;
var itemsToMoveCount = -5;
var monthsCount = 12;
var defaultMinMonth = 0;
var defaultMaxMonth = 11;
var defaultMinYear = 1900;
var defaultMaxYear = 2100;

var calculatePos = function calculatePos(pos, minPos, maxPos) {
  if (maxPos <= pos) {
    return maxPos;
  }

  if (minPos >= pos) {
    return minPos;
  }

  return pos;
};

export var DateSelectDataTids = {
  caption: 'DateSelect__caption',
  menuItem: 'DateSelect__menuItem',
  menu: 'DateSelect__menu'
};
export var DateSelect = (_dec = locale('Calendar', DatePickerLocaleHelper), responsiveLayout(_class = _dec(_class = (_temp = _class2 = /*#__PURE__*/function (_React$PureComponent) {
  _inheritsLoose(DateSelect, _React$PureComponent);

  function DateSelect() {
    var _this;

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    _this = _React$PureComponent.call.apply(_React$PureComponent, [this].concat(args)) || this;
    _this.getProps = createPropsGetter(DateSelect.defaultProps);
    _this.state = {
      botCapped: false,
      current: 0,
      height: 0,
      opened: false,
      pos: 0,
      top: 0,
      topCapped: false,
      nodeTop: Infinity
    };
    _this.root = null;
    _this.itemsContainer = null;
    _this.yearStep = 3;
    _this.touchStartY = null;

    _this.open = function () {
      if (_this.props.disabled) {
        return;
      }

      if (_this.state.opened) {
        return;
      }

      _this.setPosition(0);

      _this.setState({
        opened: true,
        current: 0
      });
    };

    _this.close = function () {
      if (!_this.state.opened) {
        return;
      }

      _this.setState({
        opened: false
      });
    };

    _this.menuId = DateSelectDataTids.menu + getRandomID();

    _this.refRoot = function (element) {
      _this.root = element;
    };

    _this.setNodeTop = function () {
      var root = _this.root;

      if (!root) {
        return;
      }

      if (_this.timeout) {
        globalObject.clearTimeout(_this.timeout);
      }

      _this.timeout = globalObject.setTimeout(function () {
        return _this.setState({
          nodeTop: getDOMRect(root).top
        });
      }, 0);
    };

    _this.refItemsContainer = function (element) {
      if (!_this.itemsContainer && element) {
        element.addEventListener('wheel', _this.handleWheel, {
          passive: false
        });
      }

      if (_this.itemsContainer && !element) {
        _this.itemsContainer.removeEventListener('wheel', _this.handleWheel);
      }

      if (isMobile) {
        if (!_this.itemsContainer && element) {
          element.addEventListener('touchstart', _this.handleTouchStart);
          element.addEventListener('touchmove', _this.handleTouchMove);
        }

        if (_this.itemsContainer && !element) {
          _this.itemsContainer.removeEventListener('touchstart', _this.handleTouchStart);

          _this.itemsContainer.removeEventListener('touchmove', _this.handleTouchMove);
        }
      }

      _this.itemsContainer = element;
    };

    _this.handleLongClickUp = function (event) {
      event.preventDefault();
      _this.longClickTimer = globalObject.setTimeout(function () {
        _this.setPositionRepeatTimer = globalObject.setInterval(function () {
          return _this.setPosition(_this.state.pos - itemHeight);
        }, 100);
      }, 200);
    };

    _this.handleLongClickDown = function (event) {
      event.preventDefault();
      _this.longClickTimer = globalObject.setTimeout(function () {
        _this.setPositionRepeatTimer = globalObject.setInterval(function () {
          return _this.setPosition(_this.state.pos + itemHeight);
        }, 100);
      }, 200);
    };

    _this.handleLongClickStop = function () {
      globalObject.clearTimeout(_this.longClickTimer);
      globalObject.clearTimeout(_this.setPositionRepeatTimer);
    };

    _this.getAnchor = function () {
      return _this.root;
    };

    _this.handleWheel = function (event) {
      if (!isInstanceOf(event, globalObject.WheelEvent)) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      var deltaY = event.deltaY;

      if (event.deltaMode === 1) {
        deltaY *= itemHeight;
      } else if (event.deltaMode === 2) {
        deltaY *= itemHeight * 4;
      }

      var pos = _this.state.pos + deltaY;

      _this.setPosition(pos);
    };

    _this.handleTouchStart = function (event) {
      if (!isInstanceOf(event, globalObject.TouchEvent)) {
        return;
      }

      _this.touchStartY = event.targetTouches[0].clientY;
    };

    _this.handleTouchMove = function (event) {
      if (!isInstanceOf(event, globalObject.TouchEvent) || !isBrowser(globalObject)) {
        return;
      }

      var clientY = event.changedTouches[0].clientY;
      var pixelRatio = globalObject.devicePixelRatio;
      var deltaY = ((_this.touchStartY || 0) - clientY) / pixelRatio;
      var pos = _this.state.pos + deltaY + deltaY / itemHeight;
      _this.touchStartY = clientY;

      _this.setPosition(pos);
    };

    _this.handleItemClick = function (shift) {
      return function () {
        var value = _this.props.value + shift;

        if (_this.props.onValueChange) {
          _this.props.onValueChange(value);
        }

        _this.setState({
          opened: false
        });
      };
    };

    _this.handleKey = function (e) {
      if (_this.state.opened && isKeyEscape(e)) {
        e.preventDefault();

        _this.close();

        e.stopPropagation();
      }
    };

    _this.handleUp = function (event) {
      event.preventDefault();

      _this.setPosition(_this.state.pos - itemHeight * _this.yearStep);
    };

    _this.handleDown = function (event) {
      event.preventDefault();

      _this.setPosition(_this.state.pos + itemHeight * _this.yearStep);
    };

    return _this;
  }

  var _proto = DateSelect.prototype;

  _proto.componentDidUpdate = function componentDidUpdate() {
    this.setNodeTop();
  };

  _proto.componentDidMount = function componentDidMount() {
    this.listener = LayoutEvents.addListener(this.setNodeTop);
    this.setNodeTop();
    globalObject.addEventListener == null ? void 0 : globalObject.addEventListener('keydown', this.handleKey);
  };

  _proto.componentWillUnmount = function componentWillUnmount() {
    if (this.listener) {
      this.listener.remove();
    }

    if (this.timeout) {
      globalObject.clearTimeout(this.timeout);
    }

    if (this.longClickTimer) {
      globalObject.clearTimeout(this.longClickTimer);
    }

    if (this.setPositionRepeatTimer) {
      globalObject.clearTimeout(this.setPositionRepeatTimer);
    }

    globalObject.removeEventListener == null ? void 0 : globalObject.removeEventListener('keydown', this.handleKey);
  }
  /**
  * @public
  */
  ;

  _proto.render = function render() {
    var _this2 = this;

    return /*#__PURE__*/React.createElement(ThemeContext.Consumer, null, function (theme) {
      _this2.theme = theme;
      return _this2.renderMain();
    });
  };

  _proto.renderMain = function renderMain() {
    var _cx, _cx2;

    if (isTheme2022(this.theme)) {
      return this.renderMain2022();
    }

    var isMobile = this.isMobileLayout;
    var disabled = this.props.disabled;
    var width = this.getProps().width;
    var isInteractiveElement = !disabled;
    var Tag = isInteractiveElement ? 'button' : 'span';
    var rootProps = {
      className: cx((_cx = {}, _cx[styles.root(this.theme)] = true, _cx[styles.disabled()] = Boolean(disabled), _cx)),
      style: {
        width: width
      },
      ref: this.refRoot,
      onClick: this.open,
      'aria-expanded': isInteractiveElement ? this.state.opened : undefined,
      'aria-controls': !disabled ? this.menuId : undefined,
      'aria-label': isInteractiveElement ? this.locale.selectChosenAriaLabel + " " + (this.getProps().type === 'year' ? this.locale.selectYearAriaLabel : this.locale.selectMonthAriaLabel) + " " + this.getItem(0) : undefined
    };
    return /*#__PURE__*/React.createElement(Tag, rootProps, /*#__PURE__*/React.createElement("div", {
      "data-tid": DateSelectDataTids.caption,
      className: styles.caption()
    }, this.getItem(0), /*#__PURE__*/React.createElement("div", {
      className: cx((_cx2 = {}, _cx2[styles.arrow(this.theme)] = true, _cx2[styles.arrowDisabled()] = Boolean(disabled), _cx2))
    }, /*#__PURE__*/React.createElement(ArrowTriangleUpDownIcon, {
      size: 12
    }))), isMobile ? !disabled && this.renderMobileMenu(this.props, this.menuId) : this.state.opened && this.renderMenu(this.menuId));
  };

  _proto.renderMain2022 = function renderMain2022() {
    var isMobile = this.isMobileLayout;
    var disabled = this.props.disabled;
    var width = this.getProps().width;
    var isInteractiveElement = !disabled;
    var Tag = isInteractiveElement ? 'button' : 'span';
    var rootProps = {
      className: cx(styles.root(this.theme), styles.root2022(), disabled && styles.disabled()),
      style: {
        width: width
      },
      ref: this.refRoot,
      onClick: this.open,
      'aria-expanded': isInteractiveElement ? this.state.opened : undefined,
      'aria-label': isInteractiveElement ? this.locale.selectChosenAriaLabel + " " + (this.getProps().type === 'year' ? this.locale.selectYearAriaLabel : this.locale.selectMonthAriaLabel) + " " + this.getItem(0) : undefined
    };
    return /*#__PURE__*/React.createElement(Tag, rootProps, /*#__PURE__*/React.createElement("div", {
      "data-tid": DateSelectDataTids.caption,
      className: styles.caption()
    }, this.getItem(0)), isInteractiveElement && /*#__PURE__*/React.createElement(ArrowCollapseCVOpenIcon16Regular, {
      className: cx(globalClasses.arrow),
      color: "#ADADAD"
    }), isMobile ? !disabled && this.renderMobileMenu(this.props, this.menuId) : this.state.opened && this.renderMenu(this.menuId));
  };

  _proto.disableItems = function disableItems(index) {
    var value = this.props.value + index;

    if (isNonNullable(this.props.maxValue) && isNonNullable(this.props.minValue)) {
      return value > this.props.maxValue || value < this.props.minValue;
    }

    if (isNonNullable(this.props.minValue)) {
      return value < this.props.minValue;
    }

    if (isNonNullable(this.props.maxValue)) {
      return value > this.props.maxValue;
    }
  };

  _proto.renderMenu = function renderMenu(id) {
    var _this3 = this,
        _cx3;

    var _this$state = this.state,
        top = _this$state.top,
        height = _this$state.height,
        nodeTop = _this$state.nodeTop;
    var shift = this.state.pos % itemHeight;

    if (shift < 0) {
      shift += itemHeight;
    }

    var from = (this.state.pos - shift + top) / itemHeight;
    var to = from + Math.ceil((height + shift) / itemHeight);
    var items = [];

    var _loop = function _loop(i) {
      var _cx4;

      var disableItems = _this3.disableItems(i) || false;
      var className = cx((_cx4 = {}, _cx4[styles.menuItem(_this3.theme)] = true, _cx4[styles.menuItemSelected(_this3.theme)] = i === 0, _cx4[styles.menuItemActive(_this3.theme)] = i === _this3.state.current, _cx4[styles.menuItemDisabled(_this3.theme)] = disableItems, _cx4));
      var clickHandler = {
        onMouseDown: preventDefault,
        onClick: _this3.handleItemClick(i)
      };
      items.push( /*#__PURE__*/React.createElement("button", _extends({
        "aria-label": "\u0412\u044B\u0431\u0440\u0430\u0442\u044C " + (_this3.getProps().type === 'year' ? 'год' : 'месяц') + " " + _this3.getItem(i),
        "data-tid": DateSelectDataTids.menuItem,
        "data-prop-disabled": disableItems,
        key: i,
        className: className,
        onMouseEnter: function onMouseEnter() {
          return _this3.setState({
            current: i
          });
        },
        onMouseLeave: function onMouseLeave() {
          return _this3.setState({
            current: null
          });
        }
      }, clickHandler), _this3.getItem(i)));
    };

    for (var i = from; i < to; ++i) {
      _loop(i);
    }

    var style = {
      top: top - 5,
      left: 0,
      right: 0
    };
    var shiftStyle = {
      position: 'relative',
      top: -shift
    };
    var holderClass = cx((_cx3 = {}, _cx3[styles.menuHolder(this.theme)] = true, _cx3[styles.isTopCapped()] = this.state.topCapped, _cx3[styles.isBotCapped()] = this.state.botCapped, _cx3));
    var dropdownOffset = -itemHeight;

    if (nodeTop < -top) {
      var overflowOffsetDelta = this.state.topCapped ? 6 : 17;
      dropdownOffset -= nodeTop + top - overflowOffsetDelta;
    }

    var iconUp = isTheme2022(this.theme) ? /*#__PURE__*/React.createElement(ArrowCUpIcon16Regular, {
      color: "#ADADAD"
    }) : /*#__PURE__*/React.createElement(ArrowChevronUpIcon, null);
    var iconDown = isTheme2022(this.theme) ? /*#__PURE__*/React.createElement(ArrowCDownIcon16Regular, {
      color: "#ADADAD"
    }) : /*#__PURE__*/React.createElement(ArrowChevronDownIcon, null);
    return /*#__PURE__*/React.createElement(RenderLayer, {
      onClickOutside: this.close,
      onFocusOutside: this.close,
      active: true
    }, /*#__PURE__*/React.createElement(DropdownContainer, {
      "data-tid": DateSelectDataTids.menu,
      id: id,
      getParent: this.getAnchor,
      offsetY: dropdownOffset,
      offsetX: -10
    }, /*#__PURE__*/React.createElement("div", {
      className: holderClass,
      style: style
    }, !this.state.topCapped && /*#__PURE__*/React.createElement("div", {
      className: cx(styles.menu(this.theme), styles.menuUp()),
      onClick: this.handleUp,
      onMouseDown: this.handleLongClickUp,
      onMouseUp: this.handleLongClickStop,
      onMouseLeave: this.handleLongClickStop,
      onTouchStart: this.handleLongClickUp,
      onTouchEnd: this.handleLongClickStop
    }, /*#__PURE__*/React.createElement("span", null, iconUp)), /*#__PURE__*/React.createElement("div", {
      className: styles.itemsHolder(),
      style: {
        height: height
      }
    }, /*#__PURE__*/React.createElement("div", {
      ref: this.refItemsContainer,
      style: shiftStyle
    }, items)), !this.state.botCapped && /*#__PURE__*/React.createElement("div", {
      className: cx(styles.menu(this.theme), styles.menuDown()),
      onClick: this.handleDown,
      onMouseDown: this.handleLongClickDown,
      onMouseUp: this.handleLongClickStop,
      onMouseLeave: this.handleLongClickStop,
      onTouchStart: this.handleLongClickDown,
      onTouchEnd: this.handleLongClickStop
    }, /*#__PURE__*/React.createElement("span", null, iconDown)))));
  };

  _proto.renderMobileMenu = function renderMobileMenu(_ref, id) {
    var _this4 = this;

    var value = _ref.value,
        minValue = _ref.minValue,
        maxValue = _ref.maxValue,
        onValueChange = _ref.onValueChange,
        type = _ref.type;
    var from = type === 'month' ? defaultMinMonth : minValue != null ? minValue : defaultMinYear;
    var to = type === 'month' ? defaultMaxMonth : maxValue != null ? maxValue : defaultMaxYear;
    var min = type === 'month' ? minValue != null ? minValue : defaultMinMonth : minValue != null ? minValue : defaultMinYear;
    var max = type === 'month' ? maxValue != null ? maxValue : defaultMaxMonth : maxValue != null ? maxValue : defaultMaxYear;
    var items = [];

    for (var item = from; item <= to; ++item) {
      items.push({
        item: item,
        disabled: item < min || item > max
      });
    }

    return (
      /*#__PURE__*/
      // eslint-disable-next-line jsx-a11y/no-onchange
      React.createElement("select", {
        id: id,
        "data-tid": type === 'month' ? CalendarDataTids.monthSelectMobile : CalendarDataTids.yearSelectMobile,
        className: styles.nativeSelect(),
        value: value,
        onChange: function onChange(e) {
          onValueChange(parseInt(e.target.value));
        }
      }, items.map(function (_ref2) {
        var _this4$locale$months;

        var item = _ref2.item,
            disabled = _ref2.disabled;
        return /*#__PURE__*/React.createElement("option", {
          key: item,
          value: item,
          disabled: disabled
        }, type === 'month' ? (_this4$locale$months = _this4.locale.months) == null ? void 0 : _this4$locale$months[item] : item);
      }))
    );
  };

  _proto.getItem = function getItem(index) {
    var value = this.props.value + index;

    if (this.getProps().type === 'month') {
      var _this$locale$months;

      return (_this$locale$months = this.locale.months) == null ? void 0 : _this$locale$months[value];
    }

    return value;
  };

  _proto.setPosition = function setPosition(pos) {
    var top = itemsToMoveCount * itemHeight;
    var height = visibleYearsCount * itemHeight;

    if (this.getProps().type === 'month') {
      top = -this.props.value * itemHeight;
      height = monthsCount * itemHeight;
    }

    var minPos = this.getMinPos() - top;
    var maxPos = this.getMaxPos() - top - height + itemHeight;
    var calculatedPos = calculatePos(pos, minPos, maxPos);
    var topCapped = calculatedPos <= minPos;
    var botCapped = calculatedPos >= maxPos;
    this.setState({
      pos: calculatedPos,
      top: top,
      height: height,
      topCapped: topCapped,
      botCapped: botCapped
    });
  };

  _proto.getMinPos = function getMinPos() {
    var type = this.getProps().type;

    if (type === 'month') {
      return -this.props.value * itemHeight;
    } else if (type === 'year') {
      return ((this.props.minValue || defaultMinYear) - this.props.value) * itemHeight;
    }

    return -Infinity; // Be defensive.
  };

  _proto.getMaxPos = function getMaxPos() {
    var type = this.getProps().type;

    if (type === 'month') {
      return (visibleYearsCount - this.props.value) * itemHeight;
    } else if (type === 'year') {
      return ((this.props.maxValue || defaultMaxYear) - this.props.value) * itemHeight;
    }

    return Infinity; // Be defensive.
  };

  return DateSelect;
}(React.PureComponent), _class2.__KONTUR_REACT_UI__ = 'DateSelect', _class2.displayName = 'DateSelect', _class2.propTypes = {
  disabled: PropTypes.bool,
  type: PropTypes.string,
  value: PropTypes.number.isRequired,
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onValueChange: PropTypes.func,
  minValue: PropTypes.number,
  maxValue: PropTypes.number
}, _class2.defaultProps = {
  type: 'year',
  width: 'auto'
}, _temp)) || _class) || _class);

function preventDefault(e) {
  e.preventDefault();
}